// State shared by the estimating database pages: which organisation, which
// version (template) and which scope is being edited, plus one list of
// records per /data collection. See helpers/estimatingDatabase/databaseRows
// for the record model.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDataByEndpoint,
  getExchangeRate,
  getDatabaseRows,
  postDatabaseRows,
  putDatabaseRows,
  deleteDatabaseRow,
} from "api/database";
import { mainAdminGetCompanies } from "api/users/mainAdminGetCompanies";
import { getCountriesAndStatesData } from "api/countries";
import { setCountries } from "actions/pricing";
import addNotification from "helpers/notify";
import { isMetricTemplate } from "helpers/estimatingDatabase/databaseUnits";
import {
  SCOPE_COMPANY,
  SCOPE_BIDLIGHT,
  isMasterView,
  databaseSelect,
  buildItems,
  isFirstLoad,
  versionFilters,
  templateOf,
  toNewRequest,
  toUpdateRequest,
  planSave,
  applyEdit,
  addItem,
  removeItems,
  setSelected,
  importRecords as importIntoItems,
  pendingCount,
  tickedRecords,
  changedCountForCopy,
  withMetricRewrite,
  currencyList,
  versionPanelFields,
  validateNewVersion,
  newVersionRequest,
  replaceTemplate,
  loadBlocker,
} from "helpers/estimatingDatabase/databaseRows";

export const PERMISSION_EDIT = "pricingDatabaseEdit";
export const PERMISSION_DELETE = "pricingDatabaseDelete";

// Collect { err } entries from POST / PUT / DELETE answers.
export function collectErrors(response) {
  if (!response) return [];
  if (Array.isArray(response.data)) {
    return response.data.filter((r) => r && r.err).map((r) => r.err);
  }
  if (response.err) return [response.err];
  return [];
}

// The country / state / city list (the 37 MB reference file) is shared with
// Pricing Catalogs through the store and fetched at most once per session.
let countriesRequest = null;

export function useDatabaseWorkspace() {
  const dispatch = useDispatch();
  const userInfo = useSelector((s) => s.user.userInfo) || {};
  const countries = useSelector((s) => (s.pricing && s.pricing.countries) || null);
  const usersPermission = useSelector((s) => s.user.userPermission);
  const companyPermission = useSelector((s) => s.user.companyPermission);
  const allPermission = useSelector((s) => s.user.allPermission);
  const permissions = useMemo(
    () =>
      (allPermission || [])
        .filter((p) => (companyPermission || []).includes(p))
        .filter((p) => (usersPermission || []).includes(p)),
    [allPermission, companyPermission, usersPermission]
  );

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(null);
  const [scope, setScope] = useState(SCOPE_COMPANY);
  const [rate, setRate] = useState(1);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const orgId = (selectedCompany && selectedCompany.value) || userInfo.company;
  const master = isMasterView(userInfo, selectedCompany && selectedCompany.value);

  useEffect(() => {
    if (!userInfo.admin) return;
    mainAdminGetCompanies()
      .then((res) =>
        setCompanies(
          ((res && res.company) || []).map((c) => ({ label: c.companyName, value: c._id }))
        )
      )
      .catch(() => setCompanies([]));
  }, [userInfo.admin]);

  // VersionUtil (AlwaysSelectCompanyData): the BidLight admin on BidLight's
  // own organisation takes Default ?? Company ?? Average, everybody else
  // Company ?? Average.
  const loadTemplates = useCallback(
    async (organizationId) => {
      if (!organizationId) return [];
      const res = await getDataByEndpoint(
        `versions?OrganizationId=${encodeURIComponent(organizationId)}`
      );
      const adminOfOrg = !!userInfo.admin && userInfo.company === organizationId;
      return ((res && res.rows) || []).map((r) => templateOf(r, adminOfOrg)).filter(Boolean);
    },
    [userInfo.admin, userInfo.company]
  );

  useEffect(() => {
    let cancelled = false;
    if (!orgId) return undefined;
    setTemplatesLoading(true);
    loadTemplates(orgId)
      .then((list) => {
        if (cancelled) return;
        setTemplates(list);
        setTemplate(list[0] || null);
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([]);
          setTemplate(null);
        }
      })
      .finally(() => !cancelled && setTemplatesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [orgId, loadTemplates]);

  useEffect(() => {
    let cancelled = false;
    const currency = template && template.Currency;
    if (!currency || currency === "USD") {
      setRate(1);
      return undefined;
    }
    getExchangeRate({ from: "USD", to: currency, value: 1 })
      .then((res) => !cancelled && setRate((res && res.value) || 1))
      .catch(() => !cancelled && setRate(1));
    return () => {
      cancelled = true;
    };
  }, [template]);

  // The desktop's reference lists (ServiceUtil.GetData) are read with the
  // organisation only, never with the version filters.
  const fetchOrgRows = useCallback(
    async (endpoint) => {
      if (!orgId) return [];
      const res = await getDatabaseRows(endpoint, orgId, {});
      return (res && res.rows) || [];
    },
    [orgId]
  );

  // ProjectTypesUtil / LaborTypesUtil: what the version's ProjectType and
  // LaborType identifiers are shown as.
  const [types, setTypes] = useState({ org: null, projectTypes: [], laborTypes: [] });
  useEffect(() => {
    let cancelled = false;
    setTypes({ org: null, projectTypes: [], laborTypes: [] });
    if (!orgId) return undefined;
    const pickAll = (rows) => (rows || []).map(databaseSelect).filter(Boolean);
    const read = (ep) => fetchOrgRows(ep).then(pickAll).catch(() => []);
    Promise.all([read("project_types"), read("labor_types")]).then(([pt, lt]) => {
      if (!cancelled) setTypes({ org: orgId, projectTypes: pt, laborTypes: lt });
    });
    return () => {
      cancelled = true;
    };
  }, [orgId, fetchOrgRows]);
  const { projectTypes, laborTypes } = types;
  const typesReady = !!orgId && types.org === orgId;

  const haveCountries = !!(countries && countries.length);
  const [countriesFailed, setCountriesFailed] = useState(false);
  const needCountries = !!(template && template.Country) && !haveCountries;
  useEffect(() => {
    if (!needCountries) return undefined;
    let cancelled = false;
    if (!countriesRequest) {
      countriesRequest = Promise.resolve()
        .then(() => getCountriesAndStatesData())
        .then((res) => (Array.isArray(res) && res.length ? res : null))
        .catch(() => null);
    }
    countriesRequest.then((list) => {
      if (list) dispatch(setCountries(list));
      else {
        countriesRequest = null;
        if (!cancelled) setCountriesFailed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [needCountries, dispatch]);

  // The version's filters resolve through these lists the way the desktop
  // does (see versionFilters), so the rows are only read once they are in.
  const ready = typesReady && (!needCountries || countriesFailed);
  const filters = useMemo(
    () =>
      versionFilters(template, {
        countries: haveCountries ? countries : null,
        projectTypes,
        laborTypes,
      }),
    [template, haveCountries, countries, projectTypes, laborTypes]
  );

  const fetchRows = useCallback(
    async (endpoint) => {
      const res = await getDatabaseRows(endpoint, orgId, filters);
      return (res && res.rows) || [];
    },
    [orgId, filters]
  );

  const currencies = useMemo(() => currencyList(countries), [countries]);

  // DatabaseTabViewModel.AddNewVersion -> VersionUtil.Add: POST the version
  // (Override=False), put it in the list in place of any same code, and make
  // it the loaded version. Returns the message to show, or null.
  const createVersion = useCallback(
    async (panel) => {
      const problem = validateNewVersion(panel, templates);
      if (problem) return problem;
      try {
        const res = await postDatabaseRows("versions", orgId, [newVersionRequest(panel, orgId)], false);
        const created = res && Array.isArray(res.data) ? res.data[0] : null;
        if (!created || created.err) {
          const msg = created && created.err ? String(created.err) : "";
          return /name already exist/i.test(msg)
            ? "Version with the same name already exist. Please use different version name."
            : "Failed to create new version. Please try again.";
        }
        const next = { ...created, isCompany: true };
        setTemplates((cur) => replaceTemplate(cur, next));
        setTemplate(next);
        return null;
      } catch (e) {
        return "Failed to create new version. Please try again.";
      }
    },
    [templates, orgId]
  );

  // ExecuteLoadItems: a version needs a country and a currency, and a
  // BidLight version (IsCompany false) first becomes the company's own
  // copy (AddNewVersion with the version's resolved fields).
  const loadVersion = useCallback(
    async (t) => {
      const blocked = loadBlocker(t, { countries });
      if (blocked) return { error: blocked };
      if (t && t.isCompany === false) {
        const panel = versionPanelFields(t, { countries, projectTypes, laborTypes });
        const error = await createVersion(panel);
        return error ? { error } : { created: true };
      }
      setTemplate(t);
      return { created: false };
    },
    [countries, projectTypes, laborTypes, createVersion]
  );

  // Writes one collection. `plan` is { post, put, del } of page records.
  const writeRows = useCallback(
    async (endpoint, plan, { toWire, toUpdateWire, targetOrgId, targetFilters, override }) => {
      const org = targetOrgId || orgId;
      const flt = targetFilters || filters;
      const errors = [];
      if (plan.post && plan.post.length) {
        const res = await postDatabaseRows(
          endpoint,
          org,
          plan.post.map((r) => toNewRequest(r, toWire, flt, org)),
          !!override
        );
        errors.push(...collectErrors(res));
      }
      if (plan.put && plan.put.length) {
        const res = await putDatabaseRows(
          endpoint,
          org,
          plan.put.map((r) => toUpdateRequest(r, toUpdateWire || toWire))
        );
        errors.push(...collectErrors(res));
      }
      for (const r of plan.del || []) {
        // eslint-disable-next-line no-await-in-loop
        const res = await deleteDatabaseRow(endpoint, r._id, org);
        errors.push(...collectErrors(res));
      }
      return errors;
    },
    [orgId, filters]
  );

  return {
    userInfo,
    permissions,
    canEdit: permissions.includes(PERMISSION_EDIT),
    canDelete: permissions.includes(PERMISSION_DELETE),
    isAdmin: !!userInfo.admin,
    companies,
    selectedCompany,
    setSelectedCompany,
    templates,
    template,
    setTemplate,
    templatesLoading,
    loadTemplates,
    scope,
    setScope,
    rate,
    orgId,
    master,
    filters,
    ready,
    fetchRows,
    fetchOrgRows,
    projectTypes,
    laborTypes,
    countries: countries || [],
    currencies,
    createVersion,
    loadVersion,
    writeRows,
  };
}

// One /data collection on a page: load, edit in memory, save.
export function useScopedList(workspace, config, { enabled = true } = {}) {
  const { endpoint, fromWire, toWire, toUpdateWire } = config;
  // isPublic is tracked like a field: ticking it makes the row an edit.
  const fieldKeys = useMemo(() => [...(config.fields || []).map((f) => f.key), "isPublic"], [config.fields]);
  const [items, setItems] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [firstLoad, setFirstLoad] = useState(false);
  const [loading, setLoading] = useState(false);

  const { fetchRows, master, scope, template, writeRows, ready = true } = workspace;

  const reload = useCallback(async () => {
    if (!enabled || !endpoint) return;
    setLoading(true);
    try {
      const rows = await fetchRows(endpoint);
      setRawRows(rows);
      setItems(buildItems(rows, { master, scope, fromWire }));
      setDeleted([]);
      setFirstLoad(isFirstLoad(rows, master));
    } catch (e) {
      addNotification(e && e.message ? e.message : "Failed to load the data. Please try again.", "danger");
      setItems([]);
    } finally {
      setLoading(false);
    }
    // fromWire is a static page function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, endpoint, fetchRows, master, scope]);

  useEffect(() => {
    if (template && ready) reload();
    else if (!template) {
      setItems([]);
      setDeleted([]);
    }
  }, [template, ready, reload]);

  const edit = useCallback(
    (key, patch) => setItems((cur) => applyEdit(cur, key, patch, fieldKeys)),
    [fieldKeys]
  );
  const add = useCallback((rec, opts) => setItems((cur) => addItem(cur, rec, opts)), []);
  const remove = useCallback(
    (keys) => {
      const res = removeItems(items, keys, scope);
      setItems(res.items);
      setDeleted((d) => [...d, ...res.deleted]);
    },
    [items, scope]
  );
  const select = useCallback(
    (keys, selected) => setItems((cur) => setSelected(cur, keys, selected)),
    []
  );
  const importRecords = useCallback(
    (records, mode, keyOf) => {
      const res = importIntoItems(items, records, { mode, keyOf, scope, fields: fieldKeys });
      setItems(res.items);
      setDeleted((d) => [...d, ...res.deleted]);
    },
    [items, scope, fieldKeys]
  );

  const generic = !!config.genericForAllCompany;

  // MaterialsDatabaseViewModel / MaterialClassDatabaseViewModel
  // .UpdateMeasurementType: on a metric version the desktop rewrites each
  // row's Unit (and a material's UnitCost) to metric in memory, which
  // makes the row an edit, so what the save actions send is the metric
  // value. Here the rows are shown and kept as stored; the rewrite is
  // applied only to what a save or copy sends.
  const metricRewrite = isMetricTemplate(template) && config.metricOnSave ? config.metricOnSave : null;
  const prepared = useMemo(() => withMetricRewrite(items, metricRewrite), [items, metricRewrite]);

  const save = useCallback(async () => {
    const plan = planSave(prepared, deleted, { scope, generic });
    const errors = await writeRows(endpoint, plan, {
      toWire,
      toUpdateWire,
      override: scope === SCOPE_BIDLIGHT,
    });
    return { errors, count: plan.post.length + plan.put.length + plan.del.length };
  }, [prepared, deleted, scope, generic, writeRows, endpoint, toWire, toUpdateWire]);

  // "Save to other version" and "Save to default"
  // (BaseDatabaseModel.SaveToOtherVersionAPI / SaveToDefaultDataAPI): ONLY
  // the ticked rows, posted with Override into another place. Nothing
  // ticked sends nothing.
  // `records` replaces the ticked rows (catalog mapping posts the crews
  // and tools under its ticked divisions).
  const copyTo = useCallback(
    async ({ targetOrgId, targetFilters, records }) => {
      const recs = records || tickedRecords(prepared);
      if (!recs.length) return { errors: [], count: 0 };
      const errors = await writeRows(
        endpoint,
        { post: recs, put: [], del: [] },
        { toWire, targetOrgId, targetFilters, override: true }
      );
      return { errors, count: recs.length };
    },
    [prepared, writeRows, endpoint, toWire]
  );

  return {
    items,
    setItems,
    deleted,
    rawRows,
    firstLoad,
    loading,
    reload,
    edit,
    add,
    remove,
    select,
    importRecords,
    save,
    copyTo,
    pending: pendingCount(items, deleted, scope),
    // GetDataChangedCount, which gates "Save to other version" / "Save to
    // default" on the desktop.
    copyChanges: changedCountForCopy(prepared, deleted, { scope, generic }),
  };
}
